import type { CSSProperties } from "react";
import { Link } from "react-router-dom";

import { Icon } from "../../components/icons";
import type { PublicDirectoryTournament } from "../../lib/api";
import { sportLabel } from "../../lib/labels";
import {
  categoriesDeadlineSummary,
  categoriesSummary,
  formatDirectoryDates,
  formatPriceFrom,
} from "../../lib/publicDirectory";
import { promoSpans, type DirectoryStatus } from "./useTournamentDirectory";
import "./TournamentCards.css";

/** Tarjetas del directorio público (inicio, /torneos y /jugador). Con
 * `withPromo`, si hay menos tarjetas que el máximo, la tarjeta «¿Organizas un
 * torneo?» ocupa el espacio que falta para que la rejilla nunca quede con
 * huecos. */
export function TournamentCards({
  tournaments,
  status,
  limit,
  withPromo = false,
  emptyText = "Pronto verás aquí los torneos con inscripciones abiertas.",
}: {
  tournaments: PublicDirectoryTournament[];
  status: DirectoryStatus;
  /** Máximo de tarjetas (el inicio muestra 4). */
  limit?: number;
  /** Llena la rejilla con PromoCard cuando hay menos de 4 torneos. */
  withPromo?: boolean;
  /** Texto cuando no hay torneos (solo sin `withPromo`). */
  emptyText?: string;
}) {
  if (status === "error") {
    return (
      <p className="t123-directory-note t123-cards__note" role="status">
        No pudimos cargar los torneos. Intenta de nuevo en un momento.
      </p>
    );
  }

  if (status === "loading") {
    const placeholders = Math.min(limit ?? 4, 4);
    return (
      <div className="t123-event-grid t123-cards" aria-busy="true">
        <p className="t123-sr-only" role="status">
          Cargando torneos…
        </p>
        {Array.from({ length: placeholders }, (_, index) => (
          <div key={index} className="t123-event-card t123-cards__skeleton" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>
        ))}
      </div>
    );
  }

  const shown = typeof limit === "number" ? tournaments.slice(0, limit) : tournaments;
  const spans = withPromo ? promoSpans(shown.length) : null;

  if (shown.length === 0 && !spans) {
    return (
      <p className="t123-directory-note t123-cards__note" role="status">
        {emptyText}
      </p>
    );
  }

  return (
    <div className="t123-event-grid t123-cards">
      {shown.map((tournament) => (
        <TournamentCard key={tournament.slug} tournament={tournament} />
      ))}
      {spans ? (
        <PromoCard
          count={shown.length}
          style={{ "--t123-promo-wide": spans.wide, "--t123-promo-medium": spans.medium } as CSSProperties}
        />
      ) : null}
    </div>
  );
}

function TournamentCard({ tournament }: { tournament: PublicDirectoryTournament }) {
  const price = formatPriceFrom(tournament.price_from);
  // Cierre por categoría (2026-10-24): una fecha si todas comparten, o una
  // línea por modalidad («Dobles · inscripciones hasta el 30 nov»).
  const summary = tournament.extemporaneous
    ? null
    : categoriesDeadlineSummary(tournament.categories, tournament.registration_deadline);
  const deadline =
    summary?.kind === "single" ? formatDirectoryDates(summary.deadline, summary.deadline) : null;
  const deadlineLines = summary?.kind === "mixed" ? summary.lines : [];
  return (
    <article className="t123-event-card">
      <div className="t123-cards__body">
        <div className="t123-card-badges">
          <span className="t123-status">Inscripciones abiertas</span>
          <span>{sportLabel(tournament.sport)}</span>
          {tournament.extemporaneous ? <span className="t123-cards__late">Extemporánea</span> : null}
        </div>
        <h3>{tournament.name}</h3>
        <ul className="t123-cards__facts">
          <li>
            <Icon name="calendar" />
            <span>{formatDirectoryDates(tournament.start_date, tournament.end_date)}</span>
          </li>
          {tournament.venue_name ? (
            <li>
              <PinIcon />
              <span>{tournament.venue_name}</span>
            </li>
          ) : null}
          <li>
            <Icon name="award" />
            <span>{categoriesSummary(tournament.categories)}</span>
          </li>
          {deadline ? (
            <li className="t123-cards__deadline">
              <ClockIcon />
              <span>Inscripciones hasta {deadline}</span>
            </li>
          ) : null}
          {deadlineLines.map((line) => (
            <li className="t123-cards__deadline" key={line}>
              <ClockIcon />
              <span>{line}</span>
            </li>
          ))}
        </ul>
        {tournament.organizer_name ? (
          <p className="t123-cards__organizer">
            Organiza: <strong>{tournament.organizer_name}</strong>
          </p>
        ) : null}
      </div>
      <div className="t123-event-card__footer">
        <span className="t123-cards__price">{price ?? ""}</span>
        <Link to={tournament.registration_url} aria-label={`Ver torneo ${tournament.name}`}>
          Ver torneo
        </Link>
      </div>
    </article>
  );
}

/** Tarjeta que completa la rejilla del inicio. Con 0 torneos funciona como
 * estado vacío; con 1 a 3 invita a los organizadores a publicar el suyo. */
export function PromoCard({
  count,
  className = "",
  style,
}: {
  /** Torneos que se muestran junto a la tarjeta. */
  count: number;
  className?: string;
  style?: CSSProperties;
}) {
  const empty = count === 0;
  return (
    <div className={`t123-promo-card${empty ? " t123-promo-card--empty" : ""} ${className}`.trim()} style={style}>
      <div className="t123-promo-card__inner">
        <span className="t123-promo-card__icon" aria-hidden="true">
          <Icon name={empty ? "calendar" : "megaphone"} />
        </span>
        <div className="t123-promo-card__copy">
          {empty ? null : <span className="t123-promo-card__kicker">¿Organizas un torneo?</span>}
          <h3>{empty ? "Pronto verás aquí torneos con inscripciones abiertas." : "Publícalo aquí"}</h3>
          {empty ? null : <p>Tus jugadores se inscriben y pagan en línea desde un solo enlace.</p>}
        </div>
        <Link className="t123-promo-card__cta" to="/organizador">
          <span>
            {empty ? "¿Organizas uno? Publícalo en Torneos123" : "Conoce cómo"}
            <span aria-hidden="true">{" ›"}</span>
          </span>
        </Link>
      </div>
      {empty ? null : <span className="t123-promo-card__ball" aria-hidden="true" />}
    </div>
  );
}

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}
